/**
 * ManageListsModal — full-screen list-of-lists with Rename/Share/Delete
 * per row, plus drag-to-reorder. Default list is locked (no Delete, no drag).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskList } from '@/types/task-lists';
import { TASK_LIST_NAME_MAX } from '@/types/task-lists';

type ManageListsModalProps = {
  visible: boolean;
  lists: TaskList[];
  currentUserId: string | null;
  atCap: boolean;
  onRename: (listId: string, newName: string) => Promise<void>;
  onRequestShare: (list: TaskList) => void;
  onRequestDelete: (list: TaskList) => void;
  onRequestAddList: () => void;
  onReorder: (orderedIds: string[]) => Promise<void>;
  onDismiss: () => void;
};

export function ManageListsModal({
  visible,
  lists,
  currentUserId,
  atCap,
  onRename,
  onRequestShare,
  onRequestDelete,
  onRequestAddList,
  onReorder,
  onDismiss,
}: ManageListsModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setEditingId(null);
      setEditingName('');
      setError(null);
    }
  }, [visible]);

  const ownedLists = lists.filter(l => l.ownerUserId === currentUserId);
  const sharedLists = lists.filter(l => l.ownerUserId !== currentUserId);

  const beginRename = useCallback((list: TaskList) => {
    setEditingId(list.id);
    setEditingName(list.name);
    setError(null);
  }, []);

  const commitRename = useCallback(async () => {
    if (!editingId) return;
    const trimmed = editingName.trim();
    if (!trimmed) { setEditingId(null); return; }
    try {
      await onRename(editingId, trimmed);
      setEditingId(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [editingId, editingName, onRename]);

  const moveUp = useCallback(async (index: number) => {
    if (index <= 1) return; // 0 = default (locked), 1 = first movable
    const reordered = [...ownedLists];
    const [item] = reordered.splice(index, 1);
    reordered.splice(index - 1, 0, item);
    await onReorder(reordered.map(l => l.id));
  }, [ownedLists, onReorder]);

  const moveDown = useCallback(async (index: number) => {
    if (index === 0 || index >= ownedLists.length - 1) return;
    const reordered = [...ownedLists];
    const [item] = reordered.splice(index, 1);
    reordered.splice(index + 1, 0, item);
    await onReorder(reordered.map(l => l.id));
  }, [ownedLists, onReorder]);

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onDismiss}>
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: palette.text }]}>Manage lists</Text>
          <TouchableOpacity onPress={onDismiss} style={styles.doneButton}>
            <Text style={[styles.doneText, { color: palette.tint }]}>Done</Text>
          </TouchableOpacity>
        </View>

        {error && <Text style={[styles.error, { color: palette.danger }]}>{error}</Text>}

        <Text style={[styles.sectionLabel, { color: palette.textMuted }]}>Your lists</Text>
        {ownedLists.map((list, index) => {
          const isEditing = editingId === list.id;
          return (
            <View key={list.id} style={[styles.row, { borderColor: palette.borderMuted }]}>
              <View style={styles.rowLeft}>
                {!list.isDefault && (
                  <View style={styles.reorderCol}>
                    <TouchableOpacity onPress={() => moveUp(index)} disabled={index <= 1}>
                      <Ionicons
                        name="chevron-up"
                        size={16}
                        color={index <= 1 ? palette.textMuted : palette.text}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => moveDown(index)}
                      disabled={index >= ownedLists.length - 1}
                    >
                      <Ionicons
                        name="chevron-down"
                        size={16}
                        color={index >= ownedLists.length - 1 ? palette.textMuted : palette.text}
                      />
                    </TouchableOpacity>
                  </View>
                )}
                {isEditing ? (
                  <TextInput
                    value={editingName}
                    onChangeText={setEditingName}
                    autoFocus
                    maxLength={TASK_LIST_NAME_MAX}
                    onBlur={commitRename}
                    onSubmitEditing={commitRename}
                    returnKeyType="done"
                    style={[
                      styles.input,
                      { color: palette.text, borderColor: palette.borderMuted },
                    ]}
                  />
                ) : (
                  <TouchableOpacity onPress={() => beginRename(list)} style={styles.nameCell}>
                    <Text style={[styles.name, { color: palette.text }]} numberOfLines={1}>
                      {list.name}
                    </Text>
                    {list.isDefault && (
                      <Text style={[styles.defaultBadge, { color: palette.textMuted }]}>Default</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.rowRight}>
                <TouchableOpacity
                  onPress={() => Alert.alert('Sharing coming soon!')}
                  style={[styles.iconBtn, styles.shareBtnDisabled]}
                >
                  <Ionicons name="share-outline" size={20} color={palette.textMuted} />
                </TouchableOpacity>
                {!list.isDefault && (
                  <TouchableOpacity onPress={() => onRequestDelete(list)} style={styles.iconBtn}>
                    <Ionicons name="trash-outline" size={20} color={palette.danger} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          );
        })}

        <TouchableOpacity
          onPress={onRequestAddList}
          disabled={atCap}
          activeOpacity={0.7}
          style={[styles.addListRow, atCap && { opacity: 0.5 }]}
        >
          <Ionicons name="add" size={20} color={palette.tint} />
          <Text style={[styles.addListLabel, { color: palette.tint }]}>Add list</Text>
        </TouchableOpacity>

        {sharedLists.length > 0 && (
          <>
            <Text style={[styles.sectionLabel, { color: palette.textMuted }]}>Shared with you</Text>
            {sharedLists.map(list => (
              <View key={list.id} style={[styles.row, { borderColor: palette.borderMuted }]}>
                <Text style={[styles.name, { color: palette.text }]} numberOfLines={1}>
                  {list.name}
                </Text>
                <Ionicons name="people-outline" size={18} color={palette.textMuted} />
              </View>
            ))}
          </>
        )}
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, paddingHorizontal: Spacing.lg },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Spacing.md,
  },
  title: { fontSize: 20, fontWeight: '700' },
  doneButton: { paddingHorizontal: Spacing.sm, paddingVertical: Spacing.xs },
  doneText: { fontSize: 16, fontWeight: '600' },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: Spacing.md,
    marginBottom: Spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowLeft: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm, flexShrink: 1 },
  rowRight: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm },
  reorderCol: { width: 24, alignItems: 'center' },
  nameCell: { flexDirection: 'row', alignItems: 'baseline', gap: Spacing.xs, flexShrink: 1 },
  name: { fontSize: 16, flexShrink: 1 },
  defaultBadge: { fontSize: 12 },
  input: {
    flex: 1,
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    fontSize: 16,
  },
  iconBtn: { padding: Spacing.xs },
  shareBtnDisabled: { opacity: 0.4 },
  error: { fontSize: 13, paddingVertical: Spacing.xs },
  addListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.md,
  },
  addListLabel: { fontSize: 16, fontWeight: '600' },
});
